// src/components/CreatePlaylistModal.js

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useProfile } from '../../context/ProfileContext'; // Adjusted path if inside components/
import toast from 'react-hot-toast';

const CreatePlaylistModal = ({ isOpen, onClose, songToAdd, source }) => {
  const [playlistName, setPlaylistName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, activeProfile, isDarkMode } = useProfile();

  useEffect(() => {
    if (isOpen) {
      // Runs when modal opens
    }
  }, [isOpen, source, songToAdd]);

  const handleCreate = async () => {
    if (activeProfile?.type === 'artist') {
      toast.error("Artist accounts cannot create playlists.");
      return;
    }

    if (!user?.uid) {
      setError('You must be logged in to create a playlist.');
      return;
    }
    if (!playlistName.trim()) {
      setError('Playlist name cannot be empty.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const db = getFirestore();
      await addDoc(collection(db, 'playlists'), {
        name: playlistName,
        name_lowercase: playlistName.toLowerCase(),
        ownerId: user.uid,
        songIds: songToAdd ? [songToAdd.id] : [],
        createdAt: serverTimestamp(),
        customArtworkUrl: null,
      });

      toast.success(`Created and added to ${playlistName}`);
      handleClose();
    } catch (err) {
      console.error("Error creating playlist:", err);
      setError('Failed to create playlist. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPlaylistName('');
    setError('');
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  // 🌟 REACT PORTAL: Mounts overlay at document.body level to eliminate parent repaints
  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 ${isDarkMode ? 'dark' : ''}`}>
          
          {/* 🌟 SOLID SEMI-TRANSPARENT BACKDROP (No GPU blur repaints) */}
          <motion.div
            className="fixed inset-0 bg-black/75 transform-gpu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "linear" }}
            onClick={handleClose}
          />

          {/* 🌟 MODAL CARD: GPU-accelerated entrance and exit transitions */}
          <motion.div
            className="relative z-10 bg-white dark:bg-brand-black rounded-3xl shadow-2xl p-8 w-full max-w-sm border border-slate-200 dark:border-brand-gray text-slate-900 dark:text-brand-white transform-gpu"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">Create New Playlist</h2>
            {error && <p className="text-brand-pink text-xs font-bold mb-4 animate-pulse">{error}</p>}
            
            <input
              type="text"
              value={playlistName}
              onChange={(e) => setPlaylistName(e.target.value)}
              placeholder="E.g. Midnight Vibes"
              autoFocus
              className="w-full p-4 bg-slate-50 dark:bg-brand-gray/40 border border-slate-300 dark:border-brand-gray rounded-2xl text-slate-900 dark:text-brand-white placeholder-slate-400 dark:placeholder-brand-white/50 focus:ring-2 focus:ring-brand-pink focus:border-transparent outline-none transition-all"
            />
            
            <div className="flex justify-end space-x-3 mt-5">
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-md text-gray-600 dark:text-brand-white/50 hover:bg-gray-100 dark:hover:bg-brand-gray/20 transition-colors text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={isLoading}
                className="px-4 py-2 rounded-md bg-brand-pink text-white font-semibold hover:brightness-90 disabled:bg-gray-400 transition-all"
              >
                {isLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CreatePlaylistModal;